import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { db } from '../Firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch, onSnapshot } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Plus, Edit2, Trash2, X, Check, Loader2, 
  Search, SlidersHorizontal, PlusCircle, MinusCircle, List,
  FolderEdit
} from 'lucide-react';

export default function ManageProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null); // null for new
  
  // Dynamic Categories state
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // Category management modal states
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [showInlineAdd, setShowInlineAdd] = useState(false);
  const [inlineCatName, setInlineCatName] = useState('');

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');

  // Form Fields
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [desc, setDesc] = useState('');
  const [img, setImg] = useState('');
  const [specs, setSpecs] = useState(['']);

  // Real-time synchronization of Categories
  useEffect(() => {
    const categoriesRef = collection(db, 'product_categories');
    
    const unsubscribe = onSnapshot(categoriesRef, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort alphabetically by category name
      list.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(list);
      setLoadingCategories(false);
    }, (err) => {
      console.error("Error loading categories:", err);
      setLoadingCategories(false);
    });

    return () => unsubscribe();
  }, []);

  // Set default category field when categories list loads
  useEffect(() => {
    if (categories.length > 0 && !category) {
      setCategory(categories[0].name);
    }
  }, [categories, category]);
  
  // CRUD Actions for Categories
  const handleAddCategory = async (e) => {
    if (e) e.preventDefault();
    if (!newCatName.trim()) {
      Swal.fire('Error', 'Category name cannot be empty.', 'error');
      return;
    }
    const nameToAdd = newCatName.trim();

    if (categories.some(c => c.name.toLowerCase() === nameToAdd.toLowerCase())) {
      Swal.fire('Error', 'Category name already exists.', 'error');
      return;
    }

    try {
      setSaving(true);
      await addDoc(collection(db, 'product_categories'), {
        name: nameToAdd,
        createdAt: new Date()
      });
      setNewCatName('');
      Swal.fire({
        title: 'Success',
        text: `Category "${nameToAdd}" created!`,
        icon: 'success',
        confirmButtonColor: '#111111',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Error creating category:", err);
      Swal.fire('Error', 'Failed to create category.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleInlineAddCategory = async () => {
    if (!inlineCatName.trim()) {
      Swal.fire('Error', 'Category name cannot be empty.', 'error');
      return;
    }
    const nameToAdd = inlineCatName.trim();

    if (categories.some(c => c.name.toLowerCase() === nameToAdd.toLowerCase())) {
      Swal.fire('Error', 'Category name already exists.', 'error');
      return;
    }

    try {
      setSaving(true);
      await addDoc(collection(db, 'product_categories'), {
        name: nameToAdd,
        createdAt: new Date()
      });
      setInlineCatName('');
      setShowInlineAdd(false);
      setCategory(nameToAdd);
      Swal.fire({
        title: 'Success',
        text: `Category "${nameToAdd}" created and selected!`,
        icon: 'success',
        confirmButtonColor: '#111111',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Error creating category:", err);
      Swal.fire('Error', 'Failed to create category.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCategory = async (catId, oldName) => {
    if (!editingCatName.trim()) {
      Swal.fire('Error', 'Category name cannot be empty.', 'error');
      return;
    }
    const newName = editingCatName.trim();
    if (newName === oldName) {
      setEditingCatId(null);
      return;
    }

    if (categories.some(c => c.name.toLowerCase() === newName.toLowerCase() && c.id !== catId)) {
      Swal.fire('Error', 'Category name already exists.', 'error');
      return;
    }

    try {
      setSaving(true);
      
      // Cascade update products
      const productsRef = collection(db, 'products');
      const querySnapshot = await getDocs(productsRef);
      const productsToUpdate = querySnapshot.docs.filter(doc => doc.data().category === oldName);

      const batch = writeBatch(db);
      
      productsToUpdate.forEach((productDoc) => {
        batch.update(doc(db, 'products', productDoc.id), { category: newName });
      });

      batch.update(doc(db, 'product_categories', catId), { name: newName });

      await batch.commit();
      
      setEditingCatId(null);
      Swal.fire({
        title: 'Success',
        text: `Category renamed to "${newName}" and updated in ${productsToUpdate.length} product(s).`,
        icon: 'success',
        confirmButtonColor: '#111111'
      });
      
      // Update local state if the currently open modal category matches
      if (category === oldName) {
        setCategory(newName);
      }
      
      // Refresh products list to show new category name immediately
      await fetchProducts();
    } catch (err) {
      console.error("Error updating category:", err);
      Swal.fire('Error', 'Failed to update category.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (catId, catName) => {
    try {
      const productsRef = collection(db, 'products');
      const querySnapshot = await getDocs(productsRef);
      const linkedProducts = querySnapshot.docs.filter(doc => doc.data().category === catName);

      if (linkedProducts.length > 0) {
        Swal.fire({
          title: 'Cannot Delete Category',
          text: `The category "${catName}" is currently assigned to ${linkedProducts.length} product(s). Please edit or delete those products first.`,
          icon: 'warning',
          confirmButtonColor: '#111111'
        });
        return;
      }

      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `You want to delete the category "${catName}"? This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#111111',
        cancelButtonColor: '#DE2929',
        confirmButtonText: 'Yes, delete it!'
      });

      if (!result.isConfirmed) return;

      setSaving(true);
      await deleteDoc(doc(db, 'product_categories', catId));
      Swal.fire('Deleted!', `Category "${catName}" has been deleted.`, 'success');
      
      if (category === catName) {
        setCategory(categories[0]?.name || '');
      }
    } catch (err) {
      console.error("Error deleting category:", err);
      Swal.fire('Error', 'Failed to delete category.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'products'));
      const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(list);
    } catch (err) {
      console.error("Error loading products:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load products from database.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const openAddModal = () => {
    setCurrentProduct(null);
    setName('');
    setCategory(categories[0]?.name || '');
    setDesc('');
    setImg('');
    setSpecs(['']);
    setShowInlineAdd(false);
    setInlineCatName('');
    setModalOpen(true);
  };

  const openEditModal = (product) => {
    setCurrentProduct(product);
    setName(product.name || '');
    setCategory(product.category || (categories[0]?.name || ''));
    setDesc(product.desc || '');
    setImg(product.img || '');
    setSpecs(product.specs && product.specs.length > 0 ? [...product.specs] : ['']);
    setShowInlineAdd(false);
    setInlineCatName('');
    setModalOpen(true);
  };

  const handleAddSpec = () => {
    setSpecs([...specs, '']);
  };

  const handleRemoveSpec = (index) => {
    if (specs.length === 1) return;
    const updated = [...specs];
    updated.splice(index, 1);
    setSpecs(updated);
  };

  const handleSpecChange = (index, val) => {
    const updated = [...specs];
    updated[index] = val;
    setSpecs(updated);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 819200) {
      Swal.fire({
        title: 'Image Too Large',
        text: 'The uploaded image exceeds the 800 KB limit. Please upload a smaller image.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImg(reader.result);
    };
    reader.onerror = (err) => {
      console.error('Image reading error:', err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to process image file.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !desc || !img || img === '/assets/logo1.png') {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Product Name, Description, and Product Image are required.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    // Check for duplicate name
    const isDuplicate = products.some(p => 
      p.name.trim().toLowerCase() === name.trim().toLowerCase() && 
      (!currentProduct || p.id !== currentProduct.id)
    );
    if (isDuplicate) {
      Swal.fire({
        title: 'Duplicate Name',
        text: 'A product with this name already exists in the catalog.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    const filteredSpecs = specs.map(s => s.trim()).filter(s => s !== '');
    if (filteredSpecs.length === 0) {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Please add at least one technical specification.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    const productData = {
      name: name.trim(),
      category,
      desc: desc.trim(),
      img: img,
      specs: filteredSpecs,
      updatedAt: new Date()
    };

    try {
      setSaving(true);
      if (currentProduct) {
        const docRef = doc(db, 'products', currentProduct.id);
        await updateDoc(docRef, productData);
        Swal.fire({
          title: 'Success',
          text: 'Product successfully updated!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      } else {
        productData.createdAt = new Date();
        await addDoc(collection(db, 'products'), productData);
        Swal.fire({
          title: 'Success',
          text: 'Product successfully created!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      }
      
      await fetchProducts();
      setTimeout(() => {
        setModalOpen(false);
      }, 1000);
    } catch (err) {
      console.error("Error saving product:", err);
      Swal.fire({
        title: 'Error',
        text: 'An error occurred while saving the product.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'products', id));
      setProducts(products.filter(p => p.id !== id));
      Swal.fire({
        title: 'Success',
        text: 'Product successfully deleted!',
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error deleting product:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete product.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          p.desc.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'All' || p.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-8 font-poppins">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-2xl font-extrabold font-montserrat text-brand-navy">Manage Products</h1>
          <p className="text-xs text-gray-500">Edit products catalog, specifications, categories, and media references.</p>
        </div>
        <div className="flex gap-3 self-start sm:self-center shrink-0">
          <button 
            onClick={() => setCategoryModalOpen(true)}
            className="flex items-center gap-2 border border-brand-silver hover:bg-brand-light text-brand-navy font-bold py-2.5 px-4 rounded-xl text-xs transition-all"
          >
            <FolderEdit size={14} className="text-brand-blue" />
            Manage Categories
          </button>
          <button 
            onClick={openAddModal}
            className="btn-primary py-2.5 px-5 text-xs flex items-center gap-2"
          >
            <Plus size={16} />
            Create Product
          </button>
        </div>
      </div>



      {/* Search & Filter Toolbar */}
      <div className="bg-white p-4 rounded-2xl border border-brand-silver/50 shadow-sm flex flex-col sm:flex-row gap-4 items-center justify-between">
        {/* Search */}
        <div className="relative w-full sm:max-w-xs">
          <input
            type="text"
            placeholder="Search by name, spec..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
          />
          <Search size={14} className="absolute left-3.5 top-3 text-gray-400" />
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <SlidersHorizontal size={14} className="text-brand-blue shrink-0" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full sm:w-48 px-3 py-2 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins bg-white"
          >
            <option value="All">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Product List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-4">
          <Loader2 className="animate-spin text-brand-blue" size={36} />
          <p className="text-xs font-bold text-gray-500">Loading products...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-3xl border border-brand-silver/50 p-12 text-center text-gray-400 max-w-md mx-auto space-y-4">
          <Package size={48} className="mx-auto text-gray-300" />
          <h3 className="text-lg font-bold font-montserrat text-brand-navy">No Products Found</h3>
          <p className="text-xs font-light text-gray-500">No products found inside the database. Please add products manually using the button below.</p>
          <button onClick={openAddModal} className="btn-primary py-2 px-4 text-xs mx-auto">Create Product</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((prod) => (
            <motion.div
              key={prod.id}
              layout
              className="bg-white rounded-2xl border border-brand-silver/60 shadow-sm overflow-hidden flex flex-col justify-between hover:shadow-md transition-all group"
            >
              {/* Product Header Card Graphic */}
              <div 
                className="h-32 bg-gradient-to-br from-brand-navy to-brand-blue p-4 flex flex-col justify-between relative"
                style={prod.img && !prod.img.startsWith('/assets/logo1.png') ? {
                  backgroundImage: `linear-gradient(to bottom, rgba(17, 17, 17, 0.2), rgba(17, 17, 17, 0.7)), url(${prod.img})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                } : {}}
              >
                <div className="absolute inset-0 bg-grid-mesh opacity-10" />
                <div className="flex justify-between items-start relative z-10">
                  <span className="bg-brand-navy/60 text-brand-green text-[9px] uppercase font-bold tracking-widest px-2.5 py-0.5 rounded border border-white/5">
                    {prod.category}
                  </span>
                  <div className="flex gap-2 bg-white/10 rounded-lg p-0.5 border border-white/5 opacity-80 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => openEditModal(prod)}
                      className="p-1 rounded text-white hover:text-brand-blue hover:bg-white transition-colors"
                      title="Edit Product"
                    >
                      <Edit2 size={12} />
                    </button>
                    <button 
                      onClick={() => handleDelete(prod.id)}
                      className="p-1 rounded text-white hover:text-red-500 hover:bg-white transition-colors"
                      title="Delete Product"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                <h4 className="text-white font-extrabold text-sm font-montserrat line-clamp-1 relative z-10 leading-snug">{prod.name}</h4>
              </div>

              {/* Product Info */}
              <div className="p-5 flex-grow flex flex-col justify-between space-y-4">
                <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{prod.desc}</p>
                
                <div className="space-y-1.5 pt-2 border-t border-brand-silver/50">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins flex items-center gap-1.5"><List size={12} /> Specifications</span>
                  <ul className="text-[10px] text-gray-600 font-poppins space-y-1 pl-1">
                    {prod.specs?.slice(0, 3).map((spec, sIdx) => (
                      <li key={sIdx} className="truncate">• {spec}</li>
                    ))}
                    {prod.specs?.length > 3 && (
                      <li className="text-brand-blue font-bold">+ {prod.specs.length - 3} more specs</li>
                    )}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* CRUD Overlay Modal */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => !saving && setModalOpen(false)}
              className="absolute inset-0 bg-black"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col max-h-[90vh]"
            >
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">
                    {currentProduct ? 'Edit Catalog Product' : 'Create Catalog Product'}
                  </h2>
                  <p className="text-xs text-gray-500">Provide configurations for product detail view.</p>
                </div>
                <button 
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex-grow overflow-y-auto p-6 space-y-6">

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Product Name */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Product Name *</label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. ICU Motorized Bed (4-Function)"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>

                  {/* Category Dropdown & Inline Add Option */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Category Segment *</label>
                      <button 
                        type="button"
                        onClick={() => setShowInlineAdd(!showInlineAdd)}
                        className="text-[10px] text-brand-blue hover:text-brand-navy flex items-center gap-1 font-bold transition-all"
                      >
                        {showInlineAdd ? "Select Existing" : "+ Create New Category"}
                      </button>
                    </div>
                    {showInlineAdd ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="New category name"
                          value={inlineCatName}
                          onChange={(e) => setInlineCatName(e.target.value)}
                          className="flex-grow px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                        />
                        <button
                          type="button"
                          onClick={handleInlineAddCategory}
                          className="px-4 py-2.5 bg-brand-navy hover:bg-brand-blue text-white text-xs font-bold rounded-xl transition-all"
                        >
                          Add
                        </button>
                      </div>
                    ) : (
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins bg-white"
                      >
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.name}>{cat.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Product Image Upload */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Product Image * (Up to 800 KB)</label>
                  {!img || img === '/assets/logo1.png' ? (
                    <div className="relative border-2 border-dashed border-brand-silver hover:border-brand-blue rounded-xl p-6 flex flex-col items-center justify-center bg-brand-light/35 transition-colors cursor-pointer">
                      <input 
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      />
                      <div className="text-center space-y-1.5">
                        <PlusCircle className="mx-auto text-gray-400" size={24} />
                        <p className="text-xs text-gray-500 font-poppins font-semibold text-brand-blue">Click to upload image</p>
                        <p className="text-[10px] text-gray-400 font-poppins">PNG, JPG or WEBP (Max 800 KB)</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 rounded-xl border border-brand-blue/20 bg-brand-blue/5 shadow-inner">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-12 rounded-lg overflow-hidden border border-brand-silver shrink-0 bg-white">
                          <img src={img} alt="Product preview" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-brand-navy font-poppins">Image uploaded successfully</p>
                          <p className="text-[10px] text-gray-500 font-poppins">Size is under 800 KB limit</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold font-poppins transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Product Description *</label>
                  <textarea 
                    rows={3}
                    required
                    placeholder="Enter key functionalities, materials used, certifications, etc."
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins resize-none leading-relaxed"
                  />
                </div>

                {/* Specifications List */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Technical Specifications *</label>
                    <button 
                      type="button" 
                      onClick={handleAddSpec}
                      className="text-xs text-brand-blue hover:text-brand-navy flex items-center gap-1 font-bold"
                    >
                      <PlusCircle size={14} /> Add Specification
                    </button>
                  </div>

                  <div className="space-y-2">
                    {specs.map((spec, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <span className="text-xs font-bold text-gray-400 shrink-0 w-4">{idx + 1}.</span>
                        <input 
                          type="text"
                          required
                          placeholder="e.g. CE/ISO Certified, 330-Degree rotational axis"
                          value={spec}
                          onChange={(e) => handleSpecChange(idx, e.target.value)}
                          className="flex-grow px-4 py-2 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                        />
                        <button 
                          type="button"
                          disabled={specs.length === 1}
                          onClick={() => handleRemoveSpec(idx)}
                          className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
                        >
                          <MinusCircle size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit Container */}
                <div className="pt-6 border-t border-brand-silver/50 flex justify-end gap-3">
                  <button 
                    type="button"
                    disabled={saving}
                    onClick={() => setModalOpen(false)}
                    className="px-5 py-2.5 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={saving}
                    className="btn-primary py-2.5 px-6 text-xs flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin" size={14} />
                        Saving product...
                      </>
                    ) : (
                      <>
                        <Check size={14} />
                        {currentProduct ? 'Save Changes' : 'Create Product'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Category Management Modal */}
      <AnimatePresence>
        {categoryModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => !saving && setCategoryModalOpen(false)}
              className="absolute inset-0 bg-black"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col max-h-[85vh]"
            >
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">
                    Manage Product Categories
                  </h2>
                  <p className="text-xs text-gray-500 font-poppins">Add, rename, or delete catalog category divisions.</p>
                </div>
                <button 
                  onClick={() => setCategoryModalOpen(false)}
                  disabled={saving}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-grow space-y-6">
                {/* Add Category Form */}
                <form onSubmit={handleAddCategory} className="space-y-2">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">
                    Create New Category
                  </label>
                  <div className="flex gap-2">
                    <input 
                      type="text"
                      required
                      placeholder="e.g. Dental Consumables"
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      className="flex-grow px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                    <button 
                      type="submit"
                      disabled={saving}
                      className="bg-brand-navy hover:bg-brand-blue text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-1 transition-all"
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                </form>

                {/* Categories List */}
                <div className="space-y-3">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold block">
                    Existing Categories ({categories.length})
                  </label>

                  {loadingCategories ? (
                    <div className="flex justify-center items-center py-8">
                      <Loader2 className="animate-spin text-brand-blue" size={24} />
                    </div>
                  ) : categories.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">No categories found. They will be seeded automatically.</p>
                  ) : (
                    <div className="border border-brand-silver/50 rounded-2xl overflow-hidden divide-y divide-brand-silver/50 max-h-[40vh] overflow-y-auto pr-1">
                      {categories.map((cat) => (
                        <div key={cat.id} className="p-3 flex items-center justify-between hover:bg-brand-light/30 transition-colors">
                          {editingCatId === cat.id ? (
                            <div className="flex gap-2 items-center w-full">
                              <input 
                                type="text"
                                value={editingCatName}
                                onChange={(e) => setEditingCatName(e.target.value)}
                                className="flex-grow px-3 py-1.5 border border-brand-silver rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-blue text-xs font-poppins"
                                autoFocus
                              />
                              <button 
                                type="button"
                                onClick={() => handleEditCategory(cat.id, cat.name)}
                                disabled={saving}
                                className="p-1.5 bg-brand-green/20 hover:bg-brand-green text-green-700 hover:text-white rounded-lg transition-all"
                                title="Save"
                              >
                                <Check size={14} />
                              </button>
                              <button 
                                type="button"
                                onClick={() => setEditingCatId(null)}
                                disabled={saving}
                                className="p-1.5 bg-red-50 hover:bg-red-500 text-red-600 hover:text-white rounded-lg transition-all"
                                title="Cancel"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="text-xs font-semibold text-brand-navy font-poppins truncate pr-4">
                                {cat.name}
                              </span>
                              <div className="flex gap-2 shrink-0">
                                <button 
                                  type="button"
                                  onClick={() => {
                                    setEditingCatId(cat.id);
                                    setEditingCatName(cat.name);
                                  }}
                                  disabled={saving}
                                  className="p-1.5 text-gray-400 hover:text-brand-blue hover:bg-brand-light rounded-lg transition-all"
                                  title="Rename Category"
                                >
                                  <Edit2 size={12} />
                                </button>
                                <button 
                                  type="button"
                                  onClick={() => handleDeleteCategory(cat.id, cat.name)}
                                  disabled={saving}
                                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                  title="Delete Category"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 border-t border-brand-silver/50 flex justify-end bg-brand-light/35">
                <button 
                  onClick={() => setCategoryModalOpen(false)}
                  disabled={saving}
                  className="px-4 py-2 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
